import React, {JSX} from "react";
import {AggregatedVisualizationResultRow} from "./ApiModels";
import {Col, OverlayTrigger, Popover, Row, Spinner, Table} from "react-bootstrap";
import { ConsoleLogger } from 'aws-amplify/utils';
import notFoundImage from "../../404_not_found_image.png";
import {ApiCallStatus} from "../CommonFunc/Enum";
import {CheckCircleFill, CursorText, Dot, PlusCircle, XCircleFill} from "react-bootstrap-icons";
import {pharmGenAggregationReportColumnDef} from "./Models";
import {
    ColumnDef,
} from "@tanstack/react-table";
import {TanStackAggregationReportTable} from "./TanStackAggregationReportTable";
import { StorageImage } from "@aws-amplify/ui-react-storage";
import { PHARM_GEN_BUCKET_NAME } from "./Const";

interface PharmGenAggregationReportProps {
    result: AggregatedVisualizationResultRow[]
    singleCurvePerPlot: boolean
    status: ApiCallStatus
}

interface PharmGenAggregationReportState {
    selectedRows: AggregatedVisualizationResultRow[]
}

const logger = new ConsoleLogger("PharmGenAggregationReport")

export class PharmGenAggregationReport extends React.Component<PharmGenAggregationReportProps, PharmGenAggregationReportState> {
    constructor(props: PharmGenAggregationReportProps) {
        super(props);
        this.state = {
            selectedRows: [],
        }
    }

    resetSelectedRow() {
        this.setState({
            selectedRows: []
        })
    }

    async addOrRemoveSelectedRows(clickedRows: AggregatedVisualizationResultRow[], isSelect: boolean) {
        if (!isSelect) {
            this.setState({selectedRows: []})
            return
        }

        let updatedSelectedRows = [...this.state.selectedRows]

        for (const row of clickedRows) {
            const existingIndex = updatedSelectedRows.findIndex(e => e.id === row.id)

            if (existingIndex >= 0) {
                continue
            } else {
                updatedSelectedRows.push(row)
            }
        }

        updatedSelectedRows = updatedSelectedRows.filter(row => clickedRows.some(clickedRow => clickedRow.id === row.id))

        this.setState({ selectedRows: updatedSelectedRows })
    }

    columnHeaderFormatter(columnName: string, displayName: string): JSX.Element {
        let popoverId: string = `${columnName}-definition`

        return <OverlayTrigger
            delay={{show: 250, hide: 400}}
            placement={"top"}
            overlay={
                <Popover className={"ms-2"}
                         id={popoverId}>
                    <Popover.Header as={"h3"}>Definition</Popover.Header>
                    <Popover.Body>
                        {pharmGenAggregationReportColumnDef[columnName]}
                    </Popover.Body>
                </Popover>
            }
        >
            <div>
                <label htmlFor={popoverId} className="link-primary">{displayName}</label>
            </div>
        </OverlayTrigger>
    }

    tableColumns: ColumnDef<AggregatedVisualizationResultRow, any>[] = [
        {
            id: "id",
            accessorKey: "id",
            header: () => this.columnHeaderFormatter("id", ""),
            enableSorting: false,
            enableColumnFilter: false,
            meta: { hidden: true },
        },
        {
            id: "jobId",
            accessorKey: "jobId",
            header: () => this.columnHeaderFormatter("jobId", ""),
            enableSorting: false,
            enableColumnFilter: false,
            meta: { hidden: true },
        },
        {
            id: "dotmaticsPlateId",
            accessorKey: "dotmaticsPlateId",
            header: () => this.columnHeaderFormatter("dotmaticsPlateId", "Dotmatics Plate ID"),
            enableSorting: true,
            enableColumnFilter: true,
        },
        {
            id: "noPlates",
            accessorKey: "noPlates",
            header: () => this.columnHeaderFormatter("noPlates", "No. Of Plates"),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            id: "dataQualityOk",
            accessorKey: "dataQualityOk",
            header: () => this.columnHeaderFormatter("dataQualityOk", "Data Quality OK?"),
            enableSorting: true,
            enableColumnFilter: false,
            cell: ({ getValue }) => {
                const value = getValue() as boolean
                if (!value) {
                    return <XCircleFill color={"#dc3545"}/>
                }
                return <CheckCircleFill color={"#198754"}/>
            },
        },
        {
            id: "qcNote",
            accessorKey: "qcNote",
            header: () => this.columnHeaderFormatter("qcNote", "Data Quality Issue"),
            enableSorting: true,
            enableColumnFilter: true,
        },
        {
            id: "cellLine",
            accessorKey: "cellLine",
            header: () => this.columnHeaderFormatter("cellLine", "Cell Line"),
            enableSorting: true,
            enableColumnFilter: true,
        },
        {
            id: "knockout",
            accessorKey: "knockout",
            header: () => this.columnHeaderFormatter("knockout", "Knockout"),
            enableSorting: true,
            enableColumnFilter: true,
        },
        {
            id: "drug",
            accessorKey: "drug",
            header: () => this.columnHeaderFormatter("drug", "Drug"),
            enableSorting: true,
            enableColumnFilter: true,
        },
        {
            id: "drugTarget",
            accessorKey: "drugTarget",
            header: () => this.columnHeaderFormatter("drugTarget", "Drug Target"),
            enableSorting: true,
            enableColumnFilter: true,
        },
        {
            id: "ic50Um",
            accessorKey: "ic50Um",
            header: () => this.columnHeaderFormatter("ic50Um", "IC50 (uM)"),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            id: "ec50Um",
            accessorKey: "ec50Um",
            header: () => this.columnHeaderFormatter("ec50Um", "EC50 (uM)"),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            id: "ec50Viability",
            accessorKey: "ec50Viability",
            header: () => this.columnHeaderFormatter("ec50Viability", "EC50 Viability Percentage"),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            id: "hillSlope",
            accessorKey: "hillSlope",
            header: () => this.columnHeaderFormatter("hillSlope", "Hill Slope"),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            id: "minViability",
            accessorKey: "minViability",
            header: () => this.columnHeaderFormatter("minViability", "Min Viability"),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            id: "drugSensitivityScore3",
            accessorKey: "drugSensitivityScore3",
            header: () => this.columnHeaderFormatter("drugSensitivityScore3", "Drug Sensitivity Score 3"),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            id: "ic20Um",
            accessorKey: "ic20Um",
            header: () => this.columnHeaderFormatter("ic20Um", "IC20 (uM)"),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            id: "ic80Um",
            accessorKey: "ic80Um",
            header: () => this.columnHeaderFormatter("ic80Um", "IC80 (uM)"),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            id: "areaUnderCurvePercentage",
            accessorKey: "areaUnderCurvePercentage",
            header: () => this.columnHeaderFormatter("areaUnderCurvePercentage", "Area Under Curve (%)"),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            id: "drugDilution",
            accessorKey: "drugDilution",
            header: () => this.columnHeaderFormatter("drugDilution", "Drug Dilution"),
            enableSorting: true,
            enableColumnFilter: true,
        },
    ]

    componentDidUpdate(prevProps: Readonly<PharmGenAggregationReportProps>, prevState: Readonly<PharmGenAggregationReportState>, snapshot?: any) {
        if (this.props.status === ApiCallStatus.Error || this.props.status === ApiCallStatus.NoData) {
            return
        }

        // First, reset selected rows if API response changes
        if (JSON.stringify(this.props.result) !== JSON.stringify(prevProps.result) ||
            this.props.singleCurvePerPlot !== prevProps.singleCurvePerPlot) {
            this.resetSelectedRow()
        }
    }

    render() {
        if (this.props.status === ApiCallStatus.NoData || this.props.status === ApiCallStatus.Error) {
            return <></>
        }

        return <>
            <Row>
                <Col md={12}>
                    {
                        this.props.status === ApiCallStatus.Loading &&
                        <>
                            <Spinner
                                animation="border"
                                size="sm"
                                variant="primary"
                            >
                            </Spinner>
                            <span className="text-primary m-2 fw-bold">
                                Up to 2 mins, narrow your search if it took too long ...
                            </span>
                        </>
                    }
                    {
                        this.props.status === ApiCallStatus.Completed &&
                        <>
                            <h4>Result</h4>
                            <TanStackAggregationReportTable
                                data={this.props.result}
                                columns={this.tableColumns}
                                isSelectedRows={this.props.singleCurvePerPlot}
                                onRowSelect={(rows, isSelect) => this.addOrRemoveSelectedRows(rows, isSelect)}
                                selectedRowsCount={this.state.selectedRows.length}
                            />
                        </>
                    }
                </Col>
            </Row>
            <Row>
            <Col md={9}>
                    <Row>
                        {
                            this.props.status === ApiCallStatus.Completed &&
                            (!this.props.singleCurvePerPlot) &&
                            <Col md={12} style={{display: "flex", justifyContent: "center", maxHeight: "20rem"}}>
                                <StorageImage
                                    path={this.props.result[0].s3Key} 
                                    alt={this.props.result[0].s3Key}
                                    bucket={PHARM_GEN_BUCKET_NAME}
                                    onGetUrlError={(e: Error) => {logger.error("get url error: ", e)}}
                                    fallbackSrc={notFoundImage}
                                    loadingElement={<>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            variant="primary"
                                        >
                                        </Spinner>
                                        <span className="text-primary m-2 fw-bold">Downloading graph ...</span>
                                    </>}
                                />
                            </Col>
                        }
                        {
                            this.props.status === ApiCallStatus.Completed &&
                            this.props.singleCurvePerPlot &&
                            this.state.selectedRows.map(selectedRow =>
                                {
                                    return <Col
                                        className={"mt-2"}
                                        key={selectedRow.id}
                                        md={this.state.selectedRows.length >= 3 ? 6 : undefined}
                                        style={{display: "flex", justifyContent: "center", maxHeight: "20rem"}}>
                                        <StorageImage 
                                            path={selectedRow.s3Key} 
                                            alt={selectedRow.dotmaticsPlateId}
                                            bucket={PHARM_GEN_BUCKET_NAME}
                                            onGetUrlError={(e: Error) => {logger.error("get url error: ", e)}}
                                            fallbackSrc={notFoundImage}
                                            loadingElement={<>
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    variant="primary"
                                                >
                                                </Spinner>
                                                <span className="text-primary m-2 fw-bold">Downloading graph ...</span>
                                            </>}
                                        />
                                    </Col>
                                }
                            )
                        }
                    </Row>
                </Col>
                {
                    this.props.status === ApiCallStatus.Completed &&
                    (this.state.selectedRows.length > 0 || !this.props.singleCurvePerPlot)
                    &&
                    <Col md={3}>
                        <h6 className={"text-primary mt-4 fw-bold"}>Legends</h6>
                        <Table striped bordered hover>
                            <tbody>
                            <tr>
                                <td><PlusCircle color="rgb(58, 58, 58)" size={30}/></td>
                                <td>DMSO</td>
                            </tr>
                            <tr>
                                <td><hr style={{height:"1px", borderTop: "3px solid rgb(58, 58, 58)", opacity: "1"}} /></td>
                                <td>Fitted curve</td>
                            </tr>
                            <tr>
                                <td><div style={{width: "4rem", height: "1.5rem", background: "rgb(58, 58, 58)", opacity: 0.1}}></div></td>
                                <td>95% confidence interval area</td>
                            </tr>
                            <tr>
                                <td><hr style={{height:"1px", borderTop: "3px dashed rgb(58, 58, 58)", opacity: "1"}} /></td>
                                <td colSpan={2}>
                                    <p>Vertical: EC50 (uM)/IC50 (uM)</p>
                                    <p>Horizontal: EC50 (uM)/IC50 (uM) viability</p>
                                </td>
                            </tr>
                            <tr>
                                <td><CursorText color={"rgb(58, 58, 58)"} size={30}/><Dot style={{marginLeft: "-1.875rem"}} size={30} /></td>
                                <td>Error bars for Standard Deviation (N=1) or Standard Error (N&gt;1)</td>
                            </tr>
                            </tbody>
                        </Table>
                    </Col>
                }
            </Row>
        </>
    }
}